import type { UIEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import times from 'lodash/times'

import { IconLoading } from '@/assets/icons/icon-loading'
import { Skeleton } from '@/components/ui/skeleton'
import { classNames } from '@/utils/helpers'

import { TagListItem } from './tags-list-item'

interface Props<T extends { name: string; id: string }> {
  list: T[]
  onClickItem: (id: T['id']) => void
  selectedItems?: T['id'][]
  actions?: JSX.Element
  listClassName?: string
  withHorizontalScroll?: boolean
  canLoadMore?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
  isLoading?: boolean
  disabledItems?: T['id'][]
  tagClassName?: string
}

export const TagList = <
  T extends {
    name: string
    id: string
    badge?: string | number
    tooltip?: string
  },
>({
  list,
  onClickItem,
  selectedItems,
  actions,
  listClassName,
  withHorizontalScroll = true,
  canLoadMore,
  onLoadMore,
  isLoadingMore,
  isLoading,
  disabledItems = [],
  tagClassName,
}: Props<T>) => {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const [isEndReached, setIsEndReached] = useState(false)

  const checkIfEndReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollWidth, scrollLeft, clientWidth } = containerRefElement

        setIsEndReached(scrollWidth - scrollLeft - clientWidth < 30)
      }
    },
    [],
  )

  useEffect(() => {
    checkIfEndReached(parentRef.current)
  }, [checkIfEndReached, list])

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollWidth, scrollLeft, clientWidth } = containerRefElement

        if (
          scrollWidth - scrollLeft - clientWidth < 100 &&
          canLoadMore &&
          onLoadMore
        ) {
          onLoadMore()
        }
      }
    },
    [onLoadMore, canLoadMore],
  )

  useEffect(() => {
    fetchMoreOnBottomReached(parentRef.current)
  }, [fetchMoreOnBottomReached])

  const handleScroll: UIEventHandler<HTMLDivElement> = (event) => {
    fetchMoreOnBottomReached(event.target as HTMLDivElement)
    checkIfEndReached(event.target as HTMLDivElement)
  }

  const isShadowVisible =
    isLoadingMore || (!isEndReached && withHorizontalScroll)

  return (
    <div className="relative max-w-full">
      <div
        onScroll={handleScroll}
        className={classNames(
          'no-scrollbar -mt-1 flex gap-2 overflow-y-auto pt-1',
          listClassName,
        )}
      >
        {isLoading
          ? times(5).map((num) => (
              <Skeleton
                key={num}
                className="dark:bg-skeleton h-8 w-[80px] shrink-0 rounded-full"
              />
            ))
          : list.map((item) => (
              <TagListItem
                key={item.id}
                item={item}
                isDisabled={disabledItems.includes(item.id)}
                isSelected={selectedItems?.includes(item.id)}
                onClick={() => onClickItem(item.id)}
                tagClassName={tagClassName}
              />
            ))}
      </div>
      {(actions || isShadowVisible) && (
        <div className="bg-card absolute bottom-0 right-0 top-0 flex items-center gap-2 pl-2">
          {isLoadingMore && <IconLoading className="animate-spin" />}
          {actions}
          {isShadowVisible && (
            <div className="absolute bottom-0 right-full top-0 w-10 bg-[linear-gradient(270deg,#FFF_0%,rgba(255,255,255,0.00)_100%)] dark:bg-[linear-gradient(270deg,#141617_0%,rgba(20,22,23,0.00)_100%)]" />
          )}
        </div>
      )}
    </div>
  )
}
